"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { CreditCard, ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "Nothing is created here",
    rule: "This page only lists entries. To add one, go to Banks — Deposit or Withdraw from an account's own page, or use the transfer icon on the Banks list",
  },
  {
    topic: "Filter tabs",
    rule: "All, Withdrawal, Deposit, and Transfer narrow the list to that type",
  },
  {
    topic: "Cancel",
    rule: "Only available on Approved rows where Transaction Type is Transfer — Deposit and Withdrawal rows don't get a Cancel action",
  },
  {
    topic: "Cancelling",
    rule: "Asks you to enter transaction details first, then a second click confirms it — there's no undo after that",
  },
  {
    topic: "Already cancelled",
    rule: "A Cancelled row has no further action available",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. One trail for every internal money movement" },
      { heading: "2. Cancelling reverses the movement, not just the record" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function BankTransferLogsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Bank Transfer Logs
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone checking or cancelling money movements between company
            accounts
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            This is the shared log for every{" "}
            <span className="geist-medium text-[#111827]">Deposit</span>,{" "}
            <span className="geist-medium text-[#111827]">Withdrawal</span>,
            and <span className="geist-medium text-[#111827]">Transfer</span>{" "}
            made from Banks. It&apos;s not a place to create one. Whichever
            account page you started from, the entry ends up here, with the
            accounts involved, the amount, who made it, and whether it&apos;s
            still standing or has been cancelled.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. One trail */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. One trail for every internal money movement
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Three different actions land in this one log, all the same kind of
            event: money moving between two accounts{" "}
            <span className="geist-medium text-[#111827]">your company controls</span>.
          </p>
          <Definitions
            items={[
              { term: "Deposit", desc: "Started from an account's own page." },
              {
                term: "Withdrawal",
                desc: "Started from another account's own page.",
              },
              { term: "Transfer", desc: "Started from the Banks list." },
            ]}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mt-3">
            This is the one place to see every internal movement without opening
            each account&apos;s history separately. The{" "}
            <span className="geist-medium text-[#111827]">
              All / Withdrawal / Deposit / Transfer
            </span>{" "}
            tabs narrow that same list down to one type at a time.
          </p>
          <Callout type="note">
            This page only reflects money that&apos;s already moved.
            There&apos;s no way to start a new Deposit, Withdrawal, or
            Transfer from here. For that, go to Banks.
          </Callout>
          <Screenshot caption="Screenshot: the log with the four filter tabs and a mix of transaction types visible." />

          {/* 2. Cancel reverses */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Cancelling reverses the movement, not just the record
          </h3>
          <Definitions
            items={[
              {
                term: "Cancel",
                desc: "Only available on Approved rows of type Transfer. Deposits and Withdrawals don't get one.",
              },
              {
                term: "Confirming",
                desc: "Asks for a reason first, then changes the row's status to Cancelled with nothing left to undo.",
              },
            ]}
          />
          <Callout type="warning">
            This wasn&apos;t tested against a real transfer, to avoid
            touching live company data. Treat cancelling as final, and check
            Bank History on both accounts afterward to confirm how the
            balances settled.
          </Callout>
          <Screenshot caption="Screenshot: the Cancel Payment confirmation with the transaction-details field." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[16px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp}>
          <Link
            href="/documentation/books/banking/banks"
            className="group flex items-center gap-3 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <CreditCard className="w-5 h-5 text-[#1785B6] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[14px] sentient-medium text-[#111827] transition-colors">
                Banks
              </p>
              <p className="text-[12px] geist-regular text-[#374151]">
                Where every Deposit, Withdrawal, and Transfer actually gets
                created
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#D4D4D4] transition-colors" />
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
